import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { PillarArticle, articleJsonLdForPillar } from "@/components/site/PillarArticle";
import { buildHead, breadcrumbJsonLd, faqJsonLd } from "@/lib/seo";
import cover from "@/assets/cover-pillar-migration.jpg";
import cover640 from "@/assets/cover-pillar-migration-640.webp";
import cover960 from "@/assets/cover-pillar-migration-960.webp";
import cover1280 from "@/assets/cover-pillar-migration-1280.webp";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const COVER_SRCSET = `${cover640} 640w, ${cover960} 960w, ${cover1280} 1280w`;
const COVER_SIZES = "(min-width: 768px) 720px, calc(100vw - 48px)";

const PATH = "/resources/shopify-replatforming-playbook";
const ALT = "/es/recursos/playbook-replatforming-shopify";
const TITLE = "Shopify Replatforming Playbook: Cutover & SEO · Shugert";
const DESC =
  "Run a Shopify replatform with controlled data migration, SEO continuity, integrations, cutover, rollback and 90-day stabilization.";
const PUBLISHED = "2026-05-19T09:00:00.000Z";
const MODIFIED = "2026-08-17T11:00:00.000Z";

const SECTIONS = [
  {
    id: "replatform-operating-model",
    heading: "Shopify replatforming operating model",
    blocks: [
      "Shopify replatforming is a governed program for replacing a commerce platform while protecting revenue, data integrity, discoverability and operations. This section defines the evidence required to move from discovery through stabilization.",
      "### Evidence required at each gate",
      "- Discovery: approved scope, exclusions, systems, owners, risks and sources of truth.",
      "- Data proof: mapping contract, exceptions, reconciliation totals and a representative sample.",
      "- Build and integration: critical journeys plus failure, retry and recovery tests.",
      "- Cutover rehearsal: timed runbook, communications, rollback and explicit go/no-go authority.",
      "- Stabilization: monitoring thresholds, triage ownership, backlog policy and exit criteria.",
      "### How is this different from the migration guide?",
      "The [Shopify migration guide](/resources/shopify-migration-guide-2026) defines what a complete migration covers. This playbook owns delivery governance, cutover and stabilization. The [Shopify Plus migration guide](/resources/shopify-plus-migration) owns Plus qualification, cost drivers and risk.",
    ],
  },
  {
    id: "why-replatform",
    heading: "Why brands replatform to Shopify",
    blocks: [
      "A replatform changes the storefront, data model, operational workflows, integrations and often the URL structure at the same time. That makes it a business program with measurable acceptance gates, not a theme project.",
      "The brands we replatform to Shopify or Shopify Plus usually share three signals: their current platform is taxing engineering velocity (every release ships behind a feature flag because the system is fragile), checkout is a known revenue leak the team can't fix in the current platform, and the merchandising team wants ownership of changes without filing a ticket.",
      "If you have only one of those, optimize what you've got. If you have all three, this guide is for you.",
    ],
  },
  {
    id: "scope-boundary",
    heading: "What this Shopify replatforming playbook owns",
    blocks: [
      "This playbook owns cross-platform program governance: workstreams, decision rights, evidence, cutover, rollback and the first 90 days after launch. It applies whether the destination is standard Shopify or Shopify Plus.",
      "It does not decide whether Shopify Plus is justified or estimate Plus-specific platform and implementation costs. Use the [Shopify Plus migration guide](/resources/shopify-plus-migration) for that planning intent, the [generic Shopify migration guide](/resources/shopify-migration-guide-2026) for the how-to checklist and the [timeline guide](/resources/shopify-migration-timeline) for duration and sequencing.",
    ],
  },
  {
    id: "four-migration-shapes",
    heading: "The four migration shapes we run",
    blocks: [
      "Not every migration looks the same. Mapping yours to one of these four shapes determines budget, timeline, and risk surface.",
      "### Shape 1, Magento / Adobe Commerce → Shopify or Shopify Plus",
      "Usually a 14–22 week build. The hard parts: catalog complexity (configurable products → variant models), multi-store hierarchies, and decades of URL structure. SEO is the headline risk.",
      "### Shape 2, BigCommerce → Shopify or Shopify Plus",
      "Often the smoothest. APIs are clean and product/customer/order shapes map closely. 8–14 weeks. The hard parts: B2B price lists, customer groups, and any Stencil-specific theme logic.",
      "### Shape 3, WooCommerce → Shopify or Shopify Plus",
      "Data is the headline. WooCommerce stores accumulate a decade of meta keys, plugin tables and inconsistent product taxonomy. Plan a 4-week data audit before estimating the build.",
      "### Shape 4, Custom stack (Rails, Node, headless CMS) → Shopify or Hydrogen",
      "These are the most expensive. The team usually has strong opinions and the migration needs to preserve a specific editorial or personalization layer. We almost always end up on Hydrogen + Plus rather than Liquid.",
    ],
  },
  {
    id: "url-seo-strategy",
    heading: "URL & SEO strategy: the 301 plan that preserves rankings",
    blocks: [
      "Shopify uses prescribed route families such as /products/ and /collections/, so a cross-platform move often changes URLs. Google documents that site moves can produce temporary ranking fluctuations while old and new URLs are recrawled; medium-sized sites can take weeks and larger sites longer. A complete URL inventory and direct redirects reduce avoidable loss, but they do not guarantee unchanged rankings. See [Google's site-move guidance](https://developers.google.com/search/docs/crawling-indexing/site-move-with-url-changes).",
      "### The non-negotiables",
      "- Crawl your current site with Screaming Frog and export every indexed URL with its rank, traffic, and backlink count.",
      "- Build a 1:1 mapping spreadsheet from old URL → new Shopify URL. Every URL with traffic or backlinks must have a target.",
      "- Implement redirects in Shopify's URL Redirects table (or Cloudflare Workers in front of it) before DNS cutover.",
      "- Avoid redirect chains. Old → new in one hop, never two.",
      "### Things that bite",
      "Faceted collection URLs, blog tag pages, internationalized URLs and search result pages all need policy. Decide which to redirect, which to canonical, and which to let 404. Document it before the cutover, not after.",
    ],
  },
  {
    id: "data-migration",
    heading: "Data migration: products, customers, orders, subscriptions",
    blocks: [
      "The migration spreadsheet has more rows than the build does. We typically move 6 data shapes and each one has gotchas.",
      "### Products & variants",
      "Shopify now supports up to 2,048 variants per product, while the product model still requires deliberate option, taxonomy and integration design. Confirm every connected app and API supports the expanded model before mapping configurable products. See the [official Shopify changelog](https://shopify.dev/changelog/the-product-variant-limit-is-now-2048-for-all-merchants).",
      "### Customers",
      "Customer CSV imports do not migrate passwords because password hashes from another platform cannot be used in Shopify. Decide whether to activate classic accounts or use passwordless customer accounts, and plan communication and consent handling before launch. See [Shopify's customer import documentation](https://help.shopify.com/en/manual/customers/import-export-customers).",
      "### Orders",
      "Historical orders can be imported via the Bulk Operations API for reference. But you usually don't want refunds, returns or subscription renewals running against the imported records, keep historicals read-only.",
      "### Subscriptions",
      "Recharge, Skio and Stay AI all have migration paths but each requires a vault re-tokenization. Schedule with your processor 6 weeks ahead.",
      "### Metafields & custom data",
      "This is where the slow week disappears. Custom data on the old platform rarely has a clean Shopify equivalent. Map it to metafields with namespaces you control, not the app's namespace.",
    ],
  },
  {
    id: "app-inventory",
    heading: "App and integrations inventory: cut, replace, keep",
    blocks: [
      "Every store we migrate has at least one app that exists because someone, 4 years ago, needed something for one campaign. The migration is the right moment to cut.",
      "### The audit",
      "List every app, every webhook, and every API consumer. For each, capture the business owner, the underlying job-to-be-done, and the monthly cost. Then sort into three columns: Keep (essential, has Shopify equivalent), Replace (Shopify has a native or better option), Cut (no longer needed).",
      "### Common cuts",
      "Custom recommendation engines (Shopify has Search & Discovery), legacy review apps (consolidate to one), legacy CRO tools that don't support new checkout. Most stores cut 20–30% of their app stack at migration.",
      "### Common keeps",
      "Klaviyo, Gorgias, the subscription processor, and the ERP/3PL integration. Plan these connections during the build, not after.",
    ],
  },
  {
    id: "program-governance",
    heading: "Program governance and acceptance gates",
    blocks: [
      "Every critical workstream needs one accountable decision owner, one evidence artifact and a written condition for closing the gate. A RACI without evidence is only a contact list.",
      "### Minimum governance record",
      "- Data: reconciliation totals, exception log and business owner sign-off.",
      "- SEO: old-to-new URL map, pre/post-launch crawl and Search Console owner.",
      "- Commerce: payment, tax, shipping, discount, refund and notification scenarios with results.",
      "- Integrations: replayable payloads, failure handling, monitoring and reconciliation owner.",
      "- Cutover: timed runbook, go/no-go authority, rollback trigger and communication channel.",
      "A phase closes only when its named evidence exists and the accountable owner accepts residual risk. Calendar dates alone do not close gates.",
    ],
  },
  {
    id: "cutover",
    heading: "The cutover: staging, freeze windows, DNS, rollback",
    blocks: [
      "Cutover is the only step the executive team will remember. Plan it like a launch, not a deploy.",
      "### Timeline",
      "- T-14 days: feature freeze on the old platform. No new content, no new products, no new code.",
      "- T-7 days: final data sync rehearsal. Time it. Document every step.",
      "- T-3 days: marketing pause on paid traffic to high-traffic landing pages.",
      "- T-0: cutover window in lowest traffic period (usually 2–6am store time on Tuesday).",
      "- T+24h: 24-hour war room. Engineering, marketing, support and the agency on shared Slack.",
      "### Rollback",
      "Keep the old platform warm for 14 days. Set TTLs low. If something catastrophic surfaces in the first 48 hours, you want to be able to revert.",
    ],
  },
  {
    id: "post-launch",
    heading: "Post-launch: monitoring, performance, the 30/60/90",
    blocks: [
      "The brands that come out of replatform happy aren't the ones that nailed launch day, they're the ones that nailed week 4.",
      "### Days 0–30",
      "Monitor everything. Track conversion rate, AOV, revenue per session, organic traffic and Core Web Vitals against the pre-launch baseline. Catch regressions in days, not weeks.",
      "### Days 30–60",
      "Ship the items you knowingly punted from launch scope. Most stores have a list of 8–15 \"phase 2\" items. Convert that into a 60-day backlog.",
      "### Days 60–90",
      "Start the optimization layer after the operation is stable. Prioritize one measurable improvement at a time so changes in conversion, performance or support volume can be attributed.",
    ],
  },
  {
    id: "what-it-costs",
    heading: "Budget controls for enterprise Shopify replatforming",
    blocks: [
      "A responsible budget is built from verified scope, not a universal platform-to-platform range. Separate one-time implementation, recurring platform and app fees, internal team allocation, contingency and post-launch ownership.",
      "### Scope controls",
      "Price the catalog and data model, storefront templates, integrations, Markets, subscriptions, B2B, SEO exposure, content volume, accessibility, analytics, QA and cutover independently. Record assumptions, exclusions and the change-control method.",
      "### Internal capacity",
      "Name business owners who can resolve catalog, operations, finance, legal, marketing and support questions inside the delivery cadence. Unavailable decision owners are a schedule dependency and should appear in the risk register.",
      "### Platform-specific cost",
      "Shopify Plus commercial terms and implementation scope are separate decisions. For current platform pricing and Plus-specific cost drivers, use the [Shopify Plus migration guide](/resources/shopify-plus-migration) and verify the [official Shopify Plus pricing page](https://www.shopify.com/plus/pricing).",
    ],
  },
];

const FAQS = [
  {
    q: "Will migration hurt my SEO?",
    a: "A site move can cause temporary ranking fluctuations while Google recrawls old and new URLs. A complete URL inventory, relevant one-hop redirects, equivalent content, crawl validation and monitoring reduce avoidable loss, but no team can guarantee unchanged rankings or a fixed recovery date.",
  },
  {
    q: "Can we keep our subscriptions live during the migration?",
    a: "Yes. We schedule a vault re-tokenization with your processor (Recharge, Skio, Stay AI) about 6 weeks before cutover. Subscriber-facing flows stay live; the change happens server-side.",
  },
  {
    q: "What's the biggest mistake teams make in migrations?",
    a: "Skipping the data audit. Teams assume their old platform's data is clean, start the build, and discover three weeks in that 12% of products have broken variant data. Always do a 1–2 week data audit before the build estimate is locked.",
  },
];

const RELATED = [
  { label: "Guide: Shopify Plus migration cost, timeline and process", href: "/resources/shopify-plus-migration" },
  { label: "Guide: Migrating to Shopify in 2026", href: "/resources/shopify-migration-guide-2026" },
  { label: "Guide: Shopify migration timeline", href: "/resources/shopify-migration-timeline" },
  { label: "Pillar: WooCommerce → Shopify migration without losing SEO", href: "/resources/woocommerce-to-shopify-migration" },
  { label: "Read all migration guides →", href: "/resources/migration" },
  { label: "Service: Shopify migration", href: "/shopify-migration" },
  { label: "Pillar: Shopify Plus Agency", href: "/shopify-plus-agency" },
];

export const Route = createFileRoute("/resources/shopify-replatforming-playbook")({
  loader: async () => { await setEdgeHtmlCache(); },
  head: () =>
    buildHead({
      locale: "en",
      title: TITLE,
      description: DESC,
      path: PATH,
      altPath: ALT,
      ogImage: cover,
      type: "article",
      author: "Samuel Noriega",
      articleAuthor: "Samuel Noriega",
      articleSection: "Migration",
      publishedTime: PUBLISHED,
      modifiedTime: MODIFIED,
      jsonLd: [
        articleJsonLdForPillar({ image: cover,
          title: TITLE,
          description: DESC,
          path: PATH,
          publishedTime: PUBLISHED,
          modifiedTime: MODIFIED,
          section: "Migration",
        }),
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources" },
          { name: "Migration", path: "/resources/migration" },
          { name: "Replatforming Playbook", path: PATH },
        ]),
        faqJsonLd(FAQS),
      ],
    }),
  component: () => (
    <SiteShell>
      <PillarArticle
        locale="en"
        path={PATH}
        eyebrow="Migration Playbook"
        guideLabel="Deep dive"
        title="Shopify Replatforming Playbook: cutover and 90-day stabilization"
        intro="An execution playbook for moving from WooCommerce, Magento, BigCommerce or a custom platform to Shopify or Shopify Plus: data, SEO continuity, integrations, cutover, rollback and stabilization."
        updated="August 2026"
        readingMinutes={20}
        hubHref="/resources/migration"
        hubLabel="Browse all migration guides"
        sections={SECTIONS}
        cover={{ src: cover, srcSet: COVER_SRCSET, sizes: COVER_SIZES, alt: "Shopify replatforming playbook, illustrated zebra cover" }}
        midCTA={{
          eyebrow: "Before you commit",
          title: "Get a migration feasibility audit",
          body: "Two weeks of senior engineering and strategy work that maps your catalog, data, URL structure, integrations and risk surface, before you sign a build SOW with anyone.",
          href: "/audit",
          label: "See the audit",
        }}
        finalCTA={{
          eyebrow: "Ready to scope",
          title: "Replatform to Shopify with a senior team",
          body: "Tell us your current platform, catalog size and the systems you can't lose. We'll send a fixed-scope migration plan with timeline, milestones and price.",
          href: "/get-a-quote?source=pillar-migration",
          label: "Request a scope",
        }}
        faqs={FAQS}
        related={RELATED}
        publishedTime={PUBLISHED}
        modifiedTime={MODIFIED}
      />
    </SiteShell>
  ),
});
